import { createReducer } from '@reduxjs/toolkit'
import { MAX_ZAP_SLIPPAGE_PERCENTAGE, MIN_ZAP_SLIPPAGE_PERCENTAGE } from 'constants/zap'

import {
  Field,
  resetMintState,
  resetSingleSidedValues,
  setSingleSided,
  setSingleSidedAmount,
  setSingleSidedCurrency,
  setStakingSlippageBPS,
  setZapSwapSlippage,
  switchFields,
  typeInput,
} from './actions'

export interface MintState {
  readonly independentField: Field
  readonly typedValue: string
  readonly otherTypedValue: string // for the case when there's no liquidity
  readonly startPriceTypedValue: string // for the case when there's no liquidity
  readonly leftRangeTypedValue: string
  readonly rightRangeTypedValue: string
  readonly singleSided: boolean
  readonly singleSidedAmount: string
  readonly singleSidedCurrency: string | undefined
  readonly zapSwapSlippage: string //(ranges from 0 - 100) in percentage
  readonly stakingSlippageBPS: number //(ranges from 0 - 100) in percentage
}

export const initialState: MintState = {
  independentField: Field.CURRENCY_A,
  typedValue: '',
  otherTypedValue: '',
  startPriceTypedValue: '',
  leftRangeTypedValue: '',
  rightRangeTypedValue: '',
  singleSided: false,
  singleSidedAmount: '',
  singleSidedCurrency: undefined,
  zapSwapSlippage: '0.5',
  stakingSlippageBPS: 100,
}

export default createReducer<MintState>(initialState, (builder) =>
  builder
    .addCase(resetMintState, () => initialState)
    .addCase(resetSingleSidedValues, (state) => {
      return {
        ...state,
        singleSided: false,
        singleSidedAmount: '',
        singleSidedCurrency: undefined,
        zapSwapSlippage: '0.5',
      }
    })
    .addCase(switchFields, (state, { payload: { independentField, typedValue, otherTypedValue } }) => {
      return {
        ...state,
        independentField: independentField === Field.CURRENCY_A ? Field.CURRENCY_B : Field.CURRENCY_A,
        typedValue: otherTypedValue,
        otherTypedValue: typedValue,
      }
    })
    .addCase(typeInput, (state, { payload: { field, typedValue, noLiquidity } }) => {
      if (noLiquidity) {
        // they're typing into the field they've last typed in
        if (field === state.independentField) {
          return {
            ...state,
            independentField: field,
            typedValue,
          }
        }
        // they're typing into a new field, store the other value
        else {
          return {
            ...state,
            independentField: field,
            typedValue,
            otherTypedValue: state.typedValue,
          }
        }
      } else {
        return {
          ...state,
          independentField: field,
          typedValue,
          otherTypedValue: '',
        }
      }
    })
    .addCase(setSingleSided, (state, { payload: { singleSided, singleSidedCurrency } }) => {
      return {
        ...state,
        singleSided,
        singleSidedCurrency,
        typedValue: '',
        otherTypedValue: '',
        singleSidedAmount: '',
      }
    })
    .addCase(setSingleSidedAmount, (state, { payload: { singleSidedAmount } }) => {
      return {
        ...state,
        singleSidedAmount,
      }
    })
    .addCase(setSingleSidedCurrency, (state, { payload: { singleSidedCurrency } }) => {
      return {
        ...state,
        singleSidedCurrency,
        singleSidedAmount: '',
      }
    })
    .addCase(setZapSwapSlippage, (state, { payload: { zapSwapSlippage } }) => {
      // Parse the slippage value and enforce bounds
      const parsedSlippage = parseFloat(zapSwapSlippage)
      let boundedSlippage = zapSwapSlippage
      if (!isNaN(parsedSlippage)) {
        if (parsedSlippage < MIN_ZAP_SLIPPAGE_PERCENTAGE) {
          boundedSlippage = MIN_ZAP_SLIPPAGE_PERCENTAGE.toString()
        } else if (parsedSlippage > MAX_ZAP_SLIPPAGE_PERCENTAGE) {
          boundedSlippage = MAX_ZAP_SLIPPAGE_PERCENTAGE.toString()
        }
      }
      return {
        ...state,
        zapSwapSlippage: boundedSlippage,
      }
    })
    .addCase(setStakingSlippageBPS, (state, { payload: { stakingSlippageBPS } }) => {
      return {
        ...state,
        stakingSlippageBPS,
      }
    })
)
